import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as Accordion from '@radix-ui/react-accordion'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, ChevronUp, MapPin, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FORM_DATE_FIELD_CLASS,
  FORM_FIELD_CLASS,
  FORM_FIELD_WITHIN_CLASS,
  FORM_TEXTAREA_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { EntityPicker } from '@/components/shared/EntityPicker'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { ClientNameWithProspect } from '@/components/shared/StatusBadges'
import { ClientFormDialog } from '@/components/clients/ClientFormDialog'
import { FormExpandLink } from '@/components/clients/FormExpandLink'
import { CategoryChip } from '@/components/contracts/form/FormControls'
import { ServiceCategoryIcon } from '@/components/icons/ServiceCategoryIcons'
import {
  AttachFilesSection,
  AddressFields,
  DatePicker,
  type AddressFieldsChange,
  type AttachmentRef,
} from '@/components/shared'
import {
  OverduePaymentWarning,
  useOverdueBlockingMode,
} from '@/components/payments/OverduePaymentWarning'
import { toast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'
import { DEFAULT_COUNTRY, resolveCountryLabel } from '@/lib/constants'
import { clientsApi } from '@/services/clients'
import { jobsApi } from '@/services/jobs'
import { contractsApi } from '@/services/contracts'
import { quotesApi } from '@/services/quotes'
import { settingsApi } from '@/services/settings'
import { techniciansApi } from '@/services/technicians'
import { getApiErrorMessage } from '@/services/api'
import { usePermissions } from '@/hooks/usePermissions'
import { useCreateJob, useJob, useUpdateJob } from '@/hooks/useJobs'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import { useServiceCategoryOptions } from '@/hooks/useServiceCategoryOptions'
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext'
import { useQueryClient } from '@tanstack/react-query'
import { Permit } from '@/components/guards/Permit'
import { AssignTechnicianSection } from '@/components/jobs/AssignTechnicianSection'
import { InterventionTypeSection } from '@/components/jobs/InterventionTypeSection'
import {
  RepeatScheduleSection,
  defaultRepeatState,
  type RepeatState,
} from '@/components/jobs/RepeatScheduleSection'
import {
  type Client,
  type Contract,
  type CreateJobRequest,
  type InterventionType,
  type Job,
  type JobPriority,
  type PaymentOption,
  type Quote,
  type RepeatScheduleRequest,
  type Technician,
} from '@/types'

const TIME_SLOTS = [
  { start: '08:00', end: '10:00' },
  { start: '10:00', end: '12:00' },
  { start: '12:00', end: '14:00' },
  { start: '14:00', end: '16:00' },
  { start: '16:00', end: '18:00' },
]
// Feedback #11 - a slot is either a fixed-card index or the 'custom' sentinel.
type SlotSelection = number | 'custom' | null

interface FormErrors {
  client_id?: string
  scheduled_date?: string
  time_slot?: string
  time_range?: string
  amount?: string
  intervention_type?: string
  original_job_id?: string
  service_type?: string
  city?: string
  postal_code?: string
  country?: string
  address?: string
}

const outlineBtnClass = 'rounded-lg px-3 py-3 text-sm font-medium leading-none'

const primaryBtnClass =
  'gap-2 rounded-lg bg-[#ff5a1f] px-3 py-3 text-sm font-medium leading-none text-white hover:bg-[#ef4f16]'

const JOB_FORM_DIALOG_ANIMATION_DURATION_MS = 200
const JOB_FORM_DIALOG_CLOSE_FALLBACK_BUFFER_MS = 50

const sectionCardClass =
  'flex min-w-[240px] shrink-0 flex-col overflow-visible rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'

const entityPickerClass = `${FORM_FIELD_CLASS} hover:bg-[#f5f6fa] data-[has-value=true]:font-normal`

const iconInputWrapperClass = FORM_FIELD_WITHIN_CLASS

const fieldInputClass = FORM_FIELD_CLASS

const iconInputInnerClass =
  'min-w-0 flex-1 border-0 bg-transparent p-0 font-sans text-sm font-normal leading-[1.4] text-[#171a20] placeholder:text-[#6c7285] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'

function parseDateParam(value: string | null): string {
  if (!value) return ''
  const normalized = value.slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : ''
}

function resolveLegacyInterventionType(
  explicit: InterventionType | null,
  paymentOption: PaymentOption,
  reason: string | null
): InterventionType | null {
  if (explicit) return explicit
  if (paymentOption === 'quote_on_site') return 'visit_quote'
  if (paymentOption === 'to_collect') return 'one_time_treatment'
  if (paymentOption === 'no_collection' && reason === 'under_contract')
    return 'under_contract'
  if (paymentOption === 'no_collection' && reason === 'under_warranty')
    return 'warranty_return'
  return null
}

// Feedback #12 - red asterisk marking a mandatory field label.
function RequiredMark() {
  return (
    <span className='text-destructive' aria-hidden='true'>
      {' '}
      *
    </span>
  )
}

function FieldLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string
  children: ReactNode
}) {
  return (
    <Label htmlFor={htmlFor} className='text-xs font-medium leading-4'>
      {children}
    </Label>
  )
}

function RequiredLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string
  children: ReactNode
}) {
  return (
    <Label htmlFor={htmlFor} className='text-xs font-medium leading-4'>
      {children}
      <RequiredMark />
    </Label>
  )
}

function SectionHeading({
  step,
  title,
  required,
  complete,
  collapsible,
}: {
  step?: number
  title: string
  required?: boolean
  complete?: boolean
  collapsible?: boolean
}) {
  const content = (
    <>
      {step !== undefined && (
        <span
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-md text-xs font-semibold leading-4 text-white',
            complete ? 'bg-[#10b981]' : 'bg-[#ff5a1f]'
          )}
        >
          {complete ? (
            <Check className='size-3.5 stroke-[2.5]' aria-hidden='true' />
          ) : (
            step
          )}
        </span>
      )}
      <h2 className='min-w-0 flex-1 font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
        {title}
        {required && <RequiredMark />}
      </h2>
      {collapsible && (
        <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f4f5f8] text-[#171a20]'>
          <ChevronDown className='size-4 group-data-[state=open]:hidden' />
          <ChevronUp className='hidden size-4 group-data-[state=open]:block' />
        </span>
      )}
    </>
  )

  if (collapsible) {
    return (
      <Accordion.Header>
        <Accordion.Trigger className='group flex h-14 w-full items-center gap-3 px-4 pb-2 pt-4 text-left data-[state=closed]:h-16 data-[state=closed]:pb-4'>
          {content}
        </Accordion.Trigger>
      </Accordion.Header>
    )
  }

  return (
    <div className='flex h-14 items-center gap-3 px-4 pb-2 pt-4'>{content}</div>
  )
}

// Form section matching the Figma create-job layout: rounded card with a
// brand-orange left accent on the section heading.
function SectionCard({
  step,
  title,
  required,
  complete,
  collapsible = false,
  defaultOpen = false,
  contentClassName,
  children,
}: {
  step?: number
  title: string
  required?: boolean
  complete?: boolean
  collapsible?: boolean
  defaultOpen?: boolean
  contentClassName?: string
  children: ReactNode
}) {
  const content = (
    <div className={cn('space-y-4 px-4 pb-4 pt-2', contentClassName)}>
      {children}
    </div>
  )

  if (collapsible) {
    return (
      <Accordion.Root
        type='single'
        collapsible
        defaultValue={defaultOpen ? 'content' : undefined}
      >
        <Accordion.Item
          value='content'
          className={cn(sectionCardClass, 'overflow-hidden')}
        >
          <SectionHeading
            step={step}
            title={title}
            required={required}
            complete={complete}
            collapsible
          />
          <Accordion.Content className='overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down'>
            {content}
          </Accordion.Content>
        </Accordion.Item>
      </Accordion.Root>
    )
  }

  return (
    <section className={sectionCardClass}>
      <SectionHeading
        step={step}
        title={title}
        required={required}
        complete={complete}
      />
      {content}
    </section>
  )
}

function RadioPill({
  selected,
  onClick,
  indicator,
  dashed,
  children,
}: {
  selected: boolean
  onClick: () => void
  indicator?: ReactNode
  dashed?: boolean
  children: ReactNode
}) {
  return (
    <button
      type='button'
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-xs font-normal leading-4 shadow-[0_1px_4px_0_rgba(38,45,66,0.05)] transition-colors',
        selected
          ? 'border-[#10b981] bg-[#ecfdf5]'
          : cn(
              'border-[#e7e9ef] bg-white hover:bg-[#f4f5f8]',
              dashed && 'border-dashed'
            )
      )}
    >
      {indicator}
      <span className='min-w-0 flex-1 leading-[1.4]'>{children}</span>
    </button>
  )
}

// 2.3.3 Create job / 2.3.10 Edit job — shared form. Embeds payment (2.3.4),
// assignment (2.3.5), and repeat schedule (2.3.15) sections.
export function JobFormDialog({
  mode,
  onClose,
  jobId,
}: {
  mode: 'create' | 'edit'
  onClose?: (destination?: string) => void
  jobId?: string
}) {
  const navigate = useNavigate()
  const { t } = useTranslation('jobs')
  const { canEdit } = usePermissions()
  const canEditAgenda = canEdit('agenda')
  const { id: routeJobId } = useParams<{ id: string }>()
  const id = jobId ?? routeJobId
  const [searchParams] = useSearchParams()
  const editing = mode === 'edit'
  const duplicateFromId = editing
    ? undefined
    : searchParams.get('duplicate_from') || undefined
  const preselectClient = searchParams.get('client_id') ?? ''
  const preselectContract = searchParams.get('contract_id') ?? ''
  const hasPreselectedContract = !editing && preselectContract !== ''

  const { data: jobData, isLoading } = useJob(editing ? id : duplicateFromId)
  useBreadcrumbLabel(editing ? t('detail.breadcrumbTitle') : undefined)
  // Tracks which job id has already hydrated the form. react-query refetches
  // (window focus, post-save invalidation) return a NEW jobData reference, so a
  // plain `[jobData]` effect would re-hydrate and clobber in-progress edits -
  // e.g. re-selecting a payment option the operator just unselected (86ey3q0x6).
  const hydratedJobId = useRef<string | null>(null)
  const hydratedFromParams = useRef(false)
  const { data: categoryOptions, isLoading: categoriesLoading } =
    useServiceCategoryOptions()
  const categoriesReady = categoryOptions !== undefined
  const create = useCreateJob()
  const update = useUpdateJob(id ?? '')
  const queryClient = useQueryClient()

  const [clientId, setClientId] = useState(preselectClient)
  // 2.7.12 - effective blocking mode for the selected client. Drives the inline
  // warning (warning mode) and the hard block when Save is clicked.
  const overdueBlockingMode = useOverdueBlockingMode(clientId || undefined)
  const [clientLabel, setClientLabel] = useState('')
  // Optional contract / quote references, filtered to the selected client.
  const [contractId, setContractId] = useState(preselectContract)
  const [quoteId, setQuoteId] = useState('')
  const [originalJobId, setOriginalJobId] = useState('')
  const [priority, setPriority] = useState<JobPriority>('medium')
  const [serviceType, setServiceType] = useState('')
  const [scheduledDate, setScheduledDate] = useState(() =>
    editing ? '' : parseDateParam(searchParams.get('scheduled_date'))
  )
  const [selectedSlot, setSelectedSlot] = useState<SlotSelection>(null)
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY)
  const [city, setCity] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [address, setAddress] = useState('')
  const [addressAdditional, setAddressAdditional] = useState('')
  const [accessCode, setAccessCode] = useState('')
  const [floor, setFloor] = useState('')
  const [accessInformation, setAccessInformation] = useState('')
  const [showAccessDetails, setShowAccessDetails] = useState(false)
  const [latitude, setLatitude] = useState<number | undefined>(undefined)
  const [longitude, setLongitude] = useState<number | undefined>(undefined)
  const [note, setNote] = useState('')
  // Feedback #13 - attachments picked/uploaded on the form. New uploads stay
  // local until the job exists; client/library picks carry a file_id to link.
  const [attachments, setAttachments] = useState<AttachmentRef[]>([])
  const persistedAttachments = useRef<{ id: string; file_id: string }[]>([])
  const [interventionType, setInterventionType] =
    useState<InterventionType | null>(
      hasPreselectedContract ? 'under_contract' : null
    )
  const [paymentOption, setPaymentOption] = useState<PaymentOption>(
    hasPreselectedContract ? 'no_collection' : null
  )
  const [amount, setAmount] = useState<number | undefined>(undefined)
  const [reason, setReason] = useState(
    hasPreselectedContract ? 'under_contract' : ''
  )
  const [technicians, setTechnicians] = useState<Technician[]>([])
  const [repeat, setRepeat] = useState<RepeatState>(defaultRepeatState)
  const [errors, setErrors] = useState<FormErrors>({})
  const [clientFormOpen, setClientFormOpen] = useState(false)
  // Let the route-backed page behind the drawer finish its first paint and
  // wait for the preloaded service categories before starting the entrance
  // animation. The category chips are therefore ready when the drawer appears.
  const [dialogOpen, setDialogOpen] = useState(false)
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null
  )
  const openFrameRef = useRef<number | null>(null)
  const closeTimerRef = useRef<number | null>(null)
  const closeDestinationRef = useRef<string | null>(null)
  const closeCompletedRef = useRef(false)
  const handledInvalidPreselectContract = useRef(false)
  const originalJobLoadRequest = useRef(0)

  const finishDialogClose = () => {
    if (closeCompletedRef.current) return
    closeCompletedRef.current = true

    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    const destination =
      closeDestinationRef.current ??
      (editing && id ? `/jobs/${id}` : '/calendar')

    if (onClose) {
      onClose(destination)
      return
    }

    navigate(destination)
  }

  const closeDialog = (destination?: string) => {
    if (!dialogOpen || closeTimerRef.current !== null) return

    closeDestinationRef.current =
      destination ?? (editing && id ? `/jobs/${id}` : '/calendar')
    setDialogOpen(false)

    // Normally navigation is driven by the panel's animationend event. Keep a
    // short fallback for reduced-motion/browser cases where it may not fire.
    closeTimerRef.current = window.setTimeout(
      () => finishDialogClose(),
      JOB_FORM_DIALOG_ANIMATION_DURATION_MS +
        JOB_FORM_DIALOG_CLOSE_FALLBACK_BUFFER_MS
    )
  }

  useEffect(() => {
    if (!categoriesReady) return

    openFrameRef.current = window.requestAnimationFrame(() => {
      openFrameRef.current = null
      setDialogOpen(true)
    })

    return () => {
      if (openFrameRef.current !== null) {
        window.cancelAnimationFrame(openFrameRef.current)
      }
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current)
      }
    }
  }, [categoriesReady])

  // Deep-links (?client_id=, ?contract_id=) fetch the selected entity details
  // independently so labels and address are available before list preload
  // completes.
  const { data: selectedClient } = useQuery({
    queryKey: ['clients', 'detail', clientId],
    queryFn: () => clientsApi.get(clientId),
    enabled: !editing && !!clientId,
  })
  const { data: selectedContract } = useQuery({
    queryKey: ['contracts', 'detail', contractId],
    queryFn: () => contractsApi.get(contractId),
    enabled: !!contractId,
  })

  useEffect(() => {
    if (editing || hydratedFromParams.current || !preselectClient) return
    const client = selectedClient?.data
    if (!client || client.id !== preselectClient) return
    hydratedFromParams.current = true
    /* eslint-disable react-hooks/set-state-in-effect */
    setClientLabel(client.name)
    setCountry(resolveCountryLabel(client.country))
    setCity(client.city ?? '')
    setPostalCode(client.postal_code ?? '')
    setAddress(client.address ?? '')
    setAddressAdditional(client.address_additional ?? '')
    setAccessCode(client.access_code ?? '')
    setFloor(client.floor ?? '')
    setAccessInformation(client.access_information ?? '')
    setShowAccessDetails(
      Boolean(
        client.address_additional ||
        client.access_code ||
        client.floor ||
        client.access_information
      )
    )
    setLatitude(client.latitude ?? undefined)
    setLongitude(client.longitude ?? undefined)
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [editing, preselectClient, selectedClient])

  useEffect(() => {
    if (editing || !preselectContract) return
    const contract = selectedContract?.data
    if (!contract) return
    const shouldClear =
      contract.status === 'terminated' || contract.client_id !== clientId
    if (!shouldClear) {
      handledInvalidPreselectContract.current = false
      return
    }

    const timer = window.setTimeout(() => {
      setContractId('')
      if (
        contract.status === 'terminated' &&
        !handledInvalidPreselectContract.current
      ) {
        handledInvalidPreselectContract.current = true
        toast({
          variant: 'destructive',
          title: t('form.terminatedContractCannotBeLinked'),
        })
      }
    }, 0)

    return () => window.clearTimeout(timer)
  }, [editing, preselectContract, selectedContract, clientId, t])

  // Feedback #80 - deep-links pre-select technician on create:
  //   technician detail → /jobs/new?technician_id=<id>
  //   availability tab  → /jobs/new?technician_id=<id>&scheduled_date=<YYYY-MM-DD>
  useEffect(() => {
    if (editing) return
    const techId = searchParams.get('technician_id')
    if (!techId) return
    let cancelled = false
    techniciansApi
      .get(techId)
      .then(res => {
        if (cancelled || !res.data) return

        setTechnicians(prev =>
          prev.some(t => t.id === res.data!.id) ? prev : [...prev, res.data!]
        )
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [editing, searchParams])

  // Hydrate edit forms and duplicate-create forms from an existing job. The
  // duplicate deliberately omits lifecycle/billing data: timers, reports,
  // payments, invoices, history and repeat schedules all start fresh.
  useEffect(() => {
    if ((editing || duplicateFromId) && jobData?.data) {
      const j = jobData.data
      if (editing && (j.status === 'in_progress' || j.status === 'completed')) {
        toast({ variant: 'destructive', title: t('form.cannotEdit') })
        navigate(`/calendar?job=${j.id}`)
        return
      }
      // Hydrate once per job id only. A background refetch of the SAME job must
      // not overwrite the operator's edits (86ey3q0x6); switching to a different
      // job id (same route) still re-hydrates.
      if (hydratedJobId.current === j.id) return
      hydratedJobId.current = j.id
      // Hydrating local form fields from the fetched job (one-shot external sync).
      /* eslint-disable react-hooks/set-state-in-effect */
      setClientId(j.client_id)
      setClientLabel(j.client?.name ?? j.client_name ?? '')
      setContractId(j.contract_id ?? '')
      setQuoteId(j.quote_id ?? '')
      setOriginalJobId(j.original_job_id ?? '')
      setPriority(j.priority)
      setServiceType(j.service_type ?? '')
      setScheduledDate(j.scheduled_date?.slice(0, 10) ?? '')
      setStartTime(j.start_time ?? '')
      setEndTime(j.end_time ?? '')
      // Feedback #11 - non-standard saved times select "Customize" instead of
      // leaving the slot unselected.
      const idx = TIME_SLOTS.findIndex(
        s => s.start === j.start_time && s.end === j.end_time
      )
      if (idx >= 0) setSelectedSlot(idx)
      else if (j.start_time && j.end_time) setSelectedSlot('custom')
      else setSelectedSlot(null)
      setCountry(resolveCountryLabel(j.country))
      setCity(j.city ?? '')
      setPostalCode(j.postal_code ?? '')
      setAddress(j.address ?? '')
      setAddressAdditional(j.address_additional ?? '')
      setAccessCode(j.access_code ?? '')
      setFloor(j.floor ?? '')
      setAccessInformation(j.access_information ?? '')
      setShowAccessDetails(
        Boolean(
          j.address_additional ||
          j.access_code ||
          j.floor ||
          j.access_information
        )
      )
      setLatitude(j.latitude ?? undefined)
      setLongitude(j.longitude ?? undefined)
      setNote(j.note ?? '')
      setInterventionType(
        resolveLegacyInterventionType(
          j.intervention_type,
          j.payment_option,
          j.no_collection_reason
        )
      )
      if (editing) {
        setPaymentOption(j.payment_option)
        setAmount(j.amount_to_collect ?? undefined)
        setReason(j.no_collection_reason ?? '')
      }
      setTechnicians(j.assigned_technicians ?? [])
      persistedAttachments.current = editing
        ? (j.attachments ?? []).map(document => ({
            id: document.id,
            file_id: document.file_id,
          }))
        : []
      setAttachments(
        (j.attachments ?? []).map(document => ({
          id: editing ? document.id : document.file_id,
          file_id: document.file_id,
          name: document.file_name,
          url: document.url,
          mime_type: document.file_type,
          size: document.file_size,
          created_at: document.created_at,
          doc_type: document.doc_type,
          source: editing
            ? document.doc_type === 'client' || document.doc_type === 'library'
              ? document.doc_type
              : 'upload'
            : 'client',
        }))
      )
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [duplicateFromId, editing, jobData, navigate, t])

  // Selecting a client pre-fills the intervention address (2.3.3-003) and
  // resets the contract / quote references (they belong to the prior client).
  const onClientSelect = (value: string, client: Client) => {
    setClientId(value)
    setClientLabel(client.name)
    setContractId('')
    setQuoteId('')
    setOriginalJobId('')
    if (interventionType === 'warranty_return') setAmount(0)
    setCountry(resolveCountryLabel(client.country))
    setCity(client.city ?? '')
    setPostalCode(client.postal_code ?? '')
    setAddress(client.address ?? '')
    setAddressAdditional(client.address_additional ?? '')
    setAccessCode(client.access_code ?? '')
    setFloor(client.floor ?? '')
    setAccessInformation(client.access_information ?? '')
    setShowAccessDetails(
      Boolean(
        client.address_additional ||
        client.access_code ||
        client.floor ||
        client.access_information
      )
    )
    setLatitude(client.latitude ?? undefined)
    setLongitude(client.longitude ?? undefined)
  }

  const onAddressChange = (next: AddressFieldsChange) => {
    if (next.country !== undefined && next.country !== country) {
      setErrors(prev => ({ ...prev, city: undefined, address: undefined }))
    }
    if (next.country !== undefined) setCountry(next.country)
    if (next.city !== undefined) setCity(next.city)
    if (next.postalCode !== undefined) setPostalCode(next.postalCode)
    if (next.address !== undefined) setAddress(next.address)
    if ('latitude' in next) setLatitude(next.latitude)
    if ('longitude' in next) setLongitude(next.longitude)
  }

  // Fixed card: derive start/end from the slot; no separate inputs shown.
  const pickSlot = (i: number) => {
    setSelectedSlot(i)
    setStartTime(TIME_SLOTS[i].start)
    setEndTime(TIME_SLOTS[i].end)
  }
  // Customize: reveal the start/end inputs and require manual entry.
  const pickCustom = () => {
    setSelectedSlot('custom')
    setStartTime('')
    setEndTime('')
  }

  const onInterventionTypeChange = (value: InterventionType) => {
    if (value === interventionType) return

    setInterventionType(value)
    setErrors(prev => ({ ...prev, intervention_type: undefined }))

    if (value === 'visit_quote') {
      setPaymentOption('quote_on_site')
      setAmount(undefined)
      setReason('')
      setContractId('')
      setOriginalJobId('')
    } else if (value === 'one_time_treatment') {
      setPaymentOption('to_collect')
      setAmount(undefined)
      setReason('')
      setContractId('')
      setOriginalJobId('')
    } else if (value === 'under_contract') {
      setPaymentOption('no_collection')
      setAmount(undefined)
      setReason('under_contract')
      setOriginalJobId('')
    } else {
      setPaymentOption('to_collect')
      setAmount(0)
      setReason('')
      setContractId('')
    }
  }

  const onOriginalJobSelect = async (value: string) => {
    const requestId = ++originalJobLoadRequest.current
    setOriginalJobId(value)
    setAmount(undefined)
    setErrors(prev => ({ ...prev, original_job_id: undefined }))

    try {
      const response = await jobsApi.get(value)
      if (requestId !== originalJobLoadRequest.current) return
      setAmount(Math.max(response.data?.invoice?.balance_due ?? 0, 0))
    } catch {
      if (requestId !== originalJobLoadRequest.current) return
      setAmount(0)
      toast({
        variant: 'destructive',
        title: t('form.originalJobLoadError'),
      })
    }
  }

  // --- Feedback #13 attach-files source wiring ---
  // Upload tab: keep the raw file locally until Save. This lets create mode show
  // Upload before a client is selected and avoids creating a client document as
  // an intermediate attachment.
  const uploadAttachment = async (file: File): Promise<AttachmentRef> => {
    return {
      name: file.name,
      mime_type: file.type,
      size: file.size,
      created_at: new Date(file.lastModified).toISOString(),
      source: 'upload',
      file,
    }
  }
  const listClientDocs = async (cid: string): Promise<AttachmentRef[]> => {
    const res = await clientsApi.documents(cid, { limit: 100 })
    return (res.data ?? []).map(d => ({
      id: d.file_id,
      file_id: d.file_id,
      name: d.file_name,
      url: d.url,
      mime_type: d.file_type,
      size: d.file_size,
      source: 'client',
    }))
  }
  const listLibraryDocs = async (): Promise<AttachmentRef[]> => {
    const res = await settingsApi.reusableDocumentOptions('job')
    return (res.data ?? []).map(d => ({
      id: d.file_id,
      file_id: d.file_id,
      name: d.name,
      source: 'library',
    }))
  }

  const renameAttachment = async (item: AttachmentRef, name: string) => {
    if (item.source !== 'upload' || !editing || !id || !item.id || item.file) {
      return
    }

    const response = await jobsApi.renameDocument(id, item.id, name)
    toast({ variant: 'success', title: t('attachments.renameSuccess') })
    return {
      name: response.data?.file_name ?? item.name,
      url: response.data?.url ?? item.url,
    }
  }

  const submit = async () => {
    const next: FormErrors = {}
    if (!clientId) next.client_id = t('form.errors.selectClient')
    if (!serviceType) next.service_type = t('form.errors.selectServiceType')
    if (!interventionType)
      next.intervention_type = t('form.errors.selectInterventionType')
    if (!scheduledDate) next.scheduled_date = t('form.errors.selectDate')
    if (selectedSlot === null) next.time_slot = t('form.errors.selectTimeSlot')
    // Feedback #11 - custom slot requires end > start.
    if (selectedSlot === 'custom') {
      if (!startTime || !endTime)
        next.time_range = t('form.errors.enterTimeRange')
      else if (endTime <= startTime)
        next.time_range = t('form.errors.endAfterStart')
    }
    // Contract collection is optional; zero and blank both skip payment.
    if (
      (paymentOption === 'to_collect' ||
        interventionType === 'under_contract') &&
      amount != null &&
      amount < 0
    )
      next.amount = t('form.errors.invalidAmount')
    if (!city.trim()) next.city = t('form.errors.enterCity')
    if (!postalCode.trim()) next.postal_code = t('form.errors.enterPostalCode')
    if (!country.trim()) next.country = t('form.errors.selectCountry')
    if (!address.trim()) next.address = t('form.errors.enterAddress')
    setErrors(next)
    if (Object.keys(next).length > 0) return

    // 2.7.12 - "Strict blocking" mode: refuse to create a job for a client with
    // unpaid invoices and surface a toast (the API also enforces this with a
    // 422). Applies to new jobs only; edits are not blocked.
    if (!editing && overdueBlockingMode === 'hard') {
      toast({
        variant: 'destructive',
        title: t('form.overdueBlocked'),
      })
      return
    }

    // Repeat schedule (2.3.15): daily / weekly / bi-weekly recurrences must be
    // bounded by an end date OR a number of occurrences (at least one); monthly
    // needs neither. Validated here (popup) before the job is created so an
    // unbounded recurrence never produces a job. Repeat is create-only.
    if (
      !editing &&
      repeat.enabled &&
      repeat.frequency !== 'monthly' &&
      !repeat.end_date &&
      !repeat.occurrences
    ) {
      toast({
        variant: 'destructive',
        title: t('form.errors.repeatBound'),
      })
      return
    }

    const effectivePaymentOption: PaymentOption =
      interventionType === 'under_contract'
        ? amount != null && amount > 0
          ? 'to_collect'
          : 'no_collection'
        : paymentOption

    const payload: CreateJobRequest = {
      client_id: clientId,
      contract_id: contractId || null,
      quote_id: quoteId || undefined,
      original_job_id:
        interventionType === 'warranty_return' ? originalJobId || null : null,
      priority,
      service_type: serviceType || undefined,
      intervention_type: interventionType as InterventionType,
      scheduled_date: scheduledDate,
      start_time: startTime,
      end_time: endTime,
      country,
      city,
      postal_code: postalCode,
      address,
      address_additional: addressAdditional,
      access_code: accessCode,
      floor,
      access_information: accessInformation,
      latitude,
      longitude,
      note: note || undefined,
      // Send explicit null (not undefined, which JSON.stringify drops) so that
      // unselecting / switching the payment option actually clears the stored
      // value on edit instead of leaving the previous one (86ey3q0x6).
      payment_option: effectivePaymentOption ?? null,
      // Send an explicit null so clearing a previously configured expected
      // amount also works in edit mode (undefined is dropped from JSON).
      amount_to_collect:
        effectivePaymentOption === 'to_collect' ||
        interventionType === 'under_contract'
          ? (amount ?? null)
          : null,
      no_collection_reason:
        effectivePaymentOption === 'no_collection'
          ? interventionType === 'under_contract'
            ? 'under_contract'
            : reason
          : null,
      technician_ids: technicians.map(t => t.id),
      lead_technician_id: duplicateFromId
        ? jobData?.data?.lead_technician_id || undefined
        : undefined,
    }
    // Repeat schedule (2.3.15) is created via a dedicated endpoint after the job
    // exists — it is NOT part of the create-job payload.
    // UI day indices are 0=Mon..6=Sun; API expects ISO weekdays 1=Mon..7=Sun.
    const repeatBody: RepeatScheduleRequest | null =
      !editing && repeat.enabled
        ? {
            frequency:
              repeat.frequency === 'biweekly' ? 'weekly' : repeat.frequency,
            interval: repeat.frequency === 'biweekly' ? 2 : 1,
            // The day selector stores 0-based indices (mon=0…sun=6), but the API
            // expects ISO weekdays (mon=1…sun=7). Shift by +1 so recurring jobs
            // land on the selected day instead of one day earlier (86ey7q6rm).
            weekdays: repeat.weekdays.map(d => d + 1),
            start_date: scheduledDate,
            end_date: repeat.end_date || undefined,
            occurrences: repeat.occurrences
              ? Number(repeat.occurrences)
              : undefined,
          }
        : null

    // Persist new picks and removals while keeping attachments already linked
    // to this job unchanged.
    const linkAttachments = async (jobId: string) => {
      let failed = false
      const existingFileIds = new Set(
        persistedAttachments.current.map(document => document.file_id)
      )
      for (const attachment of attachments) {
        try {
          if (attachment.source === 'upload' && attachment.file) {
            await jobsApi.uploadFile(jobId, attachment.file)
          } else if (
            attachment.id &&
            attachment.source !== 'upload' &&
            !existingFileIds.has(attachment.file_id ?? attachment.id)
          ) {
            await jobsApi.attachFile(jobId, attachment.id, attachment.source)
          }
        } catch {
          failed = true
        }
      }
      if (editing) {
        const selectedFileIds = new Set(
          attachments.map(attachment => attachment.file_id).filter(Boolean)
        )
        for (const document of persistedAttachments.current) {
          if (!selectedFileIds.has(document.file_id)) {
            try {
              await jobsApi.deleteDocument(jobId, document.id)
            } catch {
              failed = true
            }
          }
        }
      }
      return !failed
    }

    try {
      if (editing && id) {
        await update.mutateAsync(payload)
        const attachmentsSaved = await linkAttachments(id)
        toast({
          variant: attachmentsSaved ? 'success' : 'destructive',
          title: attachmentsSaved
            ? t('form.updateSuccess')
            : t('attachments.error'),
        })
        closeDialog(`/jobs/${id}`)
      } else {
        const created = await create.mutateAsync(payload)
        const newId = created.data?.id
        let attachmentsSaved = true
        if (newId) {
          attachmentsSaved = await linkAttachments(newId)
          if (repeatBody) {
            await jobsApi.repeat(newId, repeatBody)
            invalidatePortalMetricQueries(queryClient)
          }
        }
        toast({
          variant: attachmentsSaved ? 'success' : 'destructive',
          title: attachmentsSaved
            ? repeat.enabled
              ? t('form.recurringSuccess')
              : t('form.createSuccess')
            : t('attachments.error'),
        })
        // Non-blocking server warnings (holiday / off-hours).
        const warning = created.data?.scheduling_warning
        if (warning) {
          toast({ variant: 'warning', title: warning })
        }
        closeDialog(newId ? `/calendar?job=${newId}` : '/calendar')
      }
    } catch (e) {
      // Map server field validation (422) to inline errors — e.g. the holiday
      // block surfaces under "Schedule Date". Anything unmapped falls back to a
      // toast.
      const apiErrors = (
        e as {
          response?: { data?: { errors?: Record<string, string[] | string> } }
        }
      )?.response?.data?.errors
      const known: (keyof FormErrors)[] = [
        'client_id',
        'service_type',
        'intervention_type',
        'original_job_id',
        'scheduled_date',
        'city',
        'postal_code',
        'country',
        'address',
      ]
      const mapped: FormErrors = {}
      let hasUnmapped = false
      if (apiErrors) {
        for (const [key, val] of Object.entries(apiErrors)) {
          const msg = Array.isArray(val) ? String(val[0]) : String(val)
          if ((known as string[]).includes(key)) {
            mapped[key as keyof FormErrors] = msg
          } else {
            hasUnmapped = true
          }
        }
      }
      if (Object.keys(mapped).length) {
        setErrors(prev => ({ ...prev, ...mapped }))
      }
      if (!Object.keys(mapped).length || hasUnmapped) {
        toast({
          variant: 'destructive',
          title: getApiErrorMessage(
            e,
            editing ? t('form.updateError') : t('form.createError')
          ),
        })
      }
    }
  }

  const pageTitle = editing ? t('form.editTitle') : t('form.createTitle')
  const pending = create.isPending || update.isPending
  const clientInformationComplete = Boolean(clientId.trim())
  const interventionAddressComplete = Boolean(
    address.trim() && postalCode.trim() && city.trim() && country.trim()
  )
  const serviceComplete = Boolean(
    serviceType.trim() &&
    scheduledDate.trim() &&
    startTime.trim() &&
    endTime.trim() &&
    priority
  )
  const interventionTypeComplete = Boolean(interventionType)
  const errClass = (has: unknown) =>
    cn(
      Boolean(has) &&
        'border-destructive focus-within:ring-destructive focus-visible:ring-destructive'
    )

  const closeRoute = () => closeDialog()

  return (
    <>
      <Dialog open={dialogOpen} onOpenChange={open => !open && closeRoute()}>
        <DialogContent
          ref={setPortalContainer}
          side='right'
          aria-describedby={undefined}
          overlayClassName='bg-black/50'
          className='tf-right-drawer-long gap-0 border-l-0 shadow-[-16px_0_32px_-4px_rgba(75,80,95,0.1),-4px_0_4px_-1px_rgba(75,80,95,0.05)] will-change-[transform,opacity] [&>button]:hidden'
          onAnimationEnd={event => {
            if (
              event.target === event.currentTarget &&
              event.currentTarget.dataset.state === 'closed'
            ) {
              finishDialogClose()
            }
          }}
        >
          <header className='tf-right-drawer-header tf-right-drawer-header-accent'>
            <DialogTitle className='min-w-0 flex-1 font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
              {pageTitle}
            </DialogTitle>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='size-9 shrink-0 rounded-xl bg-[#f4f5f8] hover:bg-[#f4f5f8]'
              aria-label={t('form.cancel')}
              onClick={closeRoute}
              disabled={pending}
            >
              <X className='size-4' />
            </Button>
          </header>

          <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-[#f4f5f8] p-6'>
            {(editing || duplicateFromId) && isLoading ? (
              <>
                <Skeleton className='h-52 w-full rounded-2xl' />
                <Skeleton className='h-64 w-full rounded-2xl' />
                <Skeleton className='h-80 w-full rounded-2xl' />
              </>
            ) : (
              <>
                <SectionCard
                  step={1}
                  title={t('form.clientInformation')}
                  complete={clientInformationComplete}
                >
                  <div className='space-y-1'>
                    <RequiredLabel>{t('form.client')}</RequiredLabel>
                    <div className='flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-4'>
                      <EntityPicker<Client>
                        queryKey='clients'
                        preload
                        placeholder={t('form.searchClients')}
                        variant='search'
                        fetcher={({ search, limit, page }) =>
                          clientsApi.list({ search, limit, page })
                        }
                        getOptionLabel={c => c.name}
                        getOptionValue={c => c.id}
                        renderOption={c => (
                          <ClientNameWithProspect
                            name={c.name}
                            category={c.category}
                          />
                        )}
                        renderValue={(label, item) => (
                          <ClientNameWithProspect
                            name={label}
                            category={
                              item?.category ??
                              selectedClient?.data?.category ??
                              jobData?.data?.client?.category
                            }
                          />
                        )}
                        value={clientId || null}
                        portalContainer={portalContainer}
                        initialLabel={
                          clientLabel || selectedClient?.data?.name || undefined
                        }
                        onChange={onClientSelect}
                        className={cn(
                          entityPickerClass,
                          'flex-1 font-normal',
                          errors.client_id && 'border border-destructive'
                        )}
                      />
                      <Permit module='clients' action='edit'>
                        <button
                          type='button'
                          className='flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-dashed border-[#ff5a1f] bg-white p-3 text-sm font-medium leading-none text-[#ff5a1f] hover:bg-[#fff1ea]'
                          onClick={() => setClientFormOpen(true)}
                        >
                          <Plus className='size-4 shrink-0' />
                          {t('form.createNewClient')}
                        </button>
                      </Permit>
                    </div>
                    {errors.client_id && (
                      <p className='text-xs text-destructive'>
                        {errors.client_id}
                      </p>
                    )}
                    {/* 2.7.12 - overdue payment warning for the selected client. */}
                    <OverduePaymentWarning
                      clientId={clientId || undefined}
                      mode={overdueBlockingMode}
                    />
                  </div>

                  <div className='space-y-1'>
                    <FieldLabel>{t('form.quoteReference')}</FieldLabel>
                    <EntityPicker<Quote>
                      key={`quote-${clientId}`}
                      queryKey={`quotes-${clientId}`}
                      preload={
                        (!editing && !duplicateFromId) ||
                        (!!jobData?.data?.client_id &&
                          clientId === jobData.data.client_id)
                      }
                      placeholder={t('form.select')}
                      fetcher={({ search, limit, page }) =>
                        quotesApi.list({
                          search,
                          limit,
                          page,
                          client_id: clientId,
                        })
                      }
                      getOptionLabel={q => q.number ?? q.id.slice(0, 8)}
                      getOptionValue={q => q.id}
                      value={quoteId || null}
                      portalContainer={portalContainer}
                      onChange={v => setQuoteId(v)}
                      className={cn(
                        entityPickerClass,
                        'font-normal [&_svg]:opacity-100'
                      )}
                    />
                  </div>
                </SectionCard>

                <SectionCard
                  step={2}
                  title={t('form.interventionAddress')}
                  complete={interventionAddressComplete}
                >
                  <AddressFields
                    layout='stacked'
                    stackedLocationColumns={3}
                    country={country}
                    city={city}
                    postalCode={postalCode}
                    address={address}
                    onChange={onAddressChange}
                    controlClassName={FORM_FIELD_CLASS}
                    selectControlClassName={FORM_FIELD_CLASS}
                    portalContainer={portalContainer}
                    required
                    errors={{
                      city: errors.city,
                      address: errors.address,
                      postalCode: errors.postal_code,
                      country: errors.country,
                    }}
                  />

                  <FormExpandLink
                    expanded={showAccessDetails}
                    onToggle={() => setShowAccessDetails(value => !value)}
                    expandLabel={t('form.accessDetailsExpand')}
                    hideLabel={t('form.hide')}
                    className='self-start text-sm font-medium leading-[1.4]'
                  />

                  {showAccessDetails && (
                    <>
                      <div className='space-y-1'>
                        <FieldLabel htmlFor='address_additional'>
                          {t('form.addressDetails')}
                        </FieldLabel>
                        <div
                          className={cn(iconInputWrapperClass, 'bg-[#f5f6fa]')}
                        >
                          <MapPin className='size-4 shrink-0 text-[#171a20]' />
                          <input
                            id='address_additional'
                            value={addressAdditional}
                            onChange={event =>
                              setAddressAdditional(event.target.value)
                            }
                            placeholder={t('form.addressAdditionalPlaceholder')}
                            maxLength={150}
                            className={iconInputInnerClass}
                          />
                        </div>
                      </div>
                      <div className='grid gap-4 sm:grid-cols-2'>
                        <div className='space-y-1'>
                          <FieldLabel htmlFor='access_code'>
                            {t('form.accessCode')}
                          </FieldLabel>
                          <Input
                            id='access_code'
                            value={accessCode}
                            onChange={event =>
                              setAccessCode(event.target.value)
                            }
                            placeholder={t('form.enterHere')}
                            maxLength={50}
                            className={fieldInputClass}
                          />
                        </div>
                        <div className='space-y-1'>
                          <FieldLabel htmlFor='floor'>
                            {t('form.floor')}
                          </FieldLabel>
                          <Input
                            id='floor'
                            value={floor}
                            onChange={event => setFloor(event.target.value)}
                            placeholder={t('form.enterHere')}
                            maxLength={20}
                            className={fieldInputClass}
                          />
                        </div>
                      </div>
                      <div className='space-y-1'>
                        <FieldLabel htmlFor='access_information'>
                          {t('form.accessInformation')}
                        </FieldLabel>
                        <Textarea
                          id='access_information'
                          rows={3}
                          value={accessInformation}
                          onChange={event =>
                            setAccessInformation(event.target.value)
                          }
                          placeholder={t('form.accessInfoPlaceholder')}
                          maxLength={1000}
                          className={FORM_TEXTAREA_CLASS}
                        />
                      </div>
                    </>
                  )}
                </SectionCard>

                <SectionCard
                  step={3}
                  title={t('form.service')}
                  complete={serviceComplete}
                >
                  <div className='space-y-1'>
                    <RequiredLabel>{t('form.pestsToTreat')}</RequiredLabel>
                    {categoriesLoading ? (
                      <Skeleton className='h-10 w-full rounded-xl' />
                    ) : (
                      <div className='flex flex-wrap gap-2'>
                        {(categoryOptions ?? []).map(category => (
                          <CategoryChip
                            key={category}
                            selected={serviceType === category}
                            onSelect={() => setServiceType(category)}
                            className='px-3 text-xs font-normal leading-4'
                          >
                            <ServiceCategoryIcon name={category} />
                            {category}
                          </CategoryChip>
                        ))}
                      </div>
                    )}
                    {errors.service_type && (
                      <p className='text-xs text-destructive'>
                        {errors.service_type}
                      </p>
                    )}
                  </div>

                  <div className='grid gap-4 md:grid-cols-2'>
                    <div className='space-y-1'>
                      <RequiredLabel htmlFor='service_scheduled_date'>
                        {t('form.scheduledDate')}
                      </RequiredLabel>
                      <DatePicker
                        id='service_scheduled_date'
                        value={scheduledDate}
                        onChange={setScheduledDate}
                        variant='field'
                        error={!!errors.scheduled_date}
                        className={FORM_DATE_FIELD_CLASS}
                      />
                      {errors.scheduled_date && (
                        <p className='text-xs text-destructive'>
                          {errors.scheduled_date}
                        </p>
                      )}
                    </div>

                    <div className='space-y-1'>
                      <RequiredLabel>{t('form.timeSlot')}</RequiredLabel>
                      <div className='flex flex-wrap gap-2'>
                        {TIME_SLOTS.map((slot, index) => (
                          <RadioPill
                            key={`${slot.start}-${slot.end}`}
                            selected={selectedSlot === index}
                            onClick={() => pickSlot(index)}
                          >
                            {slot.start} - {slot.end}
                          </RadioPill>
                        ))}
                        <RadioPill
                          selected={selectedSlot === 'custom'}
                          onClick={pickCustom}
                          indicator={<Plus className='size-4 shrink-0' />}
                          dashed
                        >
                          {t('form.timeSlotCustomize')}
                        </RadioPill>
                      </div>
                      {errors.time_slot && (
                        <p className='text-xs text-destructive'>
                          {errors.time_slot}
                        </p>
                      )}
                    </div>
                  </div>

                  {selectedSlot === 'custom' && (
                    <div className='grid gap-4 rounded-xl bg-[#f4f5f8] p-4 sm:grid-cols-2'>
                      <div className='space-y-1'>
                        <RequiredLabel htmlFor='start_time'>
                          {t('form.startTime')}
                        </RequiredLabel>
                        <div
                          className={cn(
                            iconInputWrapperClass,
                            'bg-white',
                            errClass(errors.time_range)
                          )}
                        >
                          <input
                            id='start_time'
                            type='time'
                            value={startTime}
                            onChange={event => setStartTime(event.target.value)}
                            className={iconInputInnerClass}
                          />
                        </div>
                      </div>
                      <div className='space-y-1'>
                        <RequiredLabel htmlFor='end_time'>
                          {t('form.endTime')}
                        </RequiredLabel>
                        <div
                          className={cn(
                            iconInputWrapperClass,
                            'bg-white',
                            errClass(errors.time_range)
                          )}
                        >
                          <input
                            id='end_time'
                            type='time'
                            value={endTime}
                            onChange={event => setEndTime(event.target.value)}
                            className={iconInputInnerClass}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {errors.time_range && (
                    <p className='text-xs text-destructive'>
                      {errors.time_range}
                    </p>
                  )}

                  <div className='space-y-1'>
                    <RequiredLabel>{t('form.priority')}</RequiredLabel>
                    <div className='flex flex-wrap gap-2'>
                      {(
                        ['low', 'medium', 'high', 'urgent'] as JobPriority[]
                      ).map(value => (
                        <RadioPill
                          key={value}
                          selected={priority === value}
                          onClick={() => setPriority(value)}
                          indicator={
                            <span
                              className={cn(
                                'size-2 shrink-0 rounded-full',
                                value === 'low' && 'bg-[#10b981]',
                                value === 'medium' && 'bg-[#3b82f6]',
                                value === 'high' && 'bg-[#f59e0b]',
                                value === 'urgent' && 'bg-[#ef4444]'
                              )}
                            />
                          }
                        >
                          {t(`form.priorities.${value}`)}
                        </RadioPill>
                      ))}
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  step={4}
                  title={t('form.interventionType')}
                  complete={interventionTypeComplete}
                >
                  <InterventionTypeSection
                    value={interventionType}
                    onChange={onInterventionTypeChange}
                    amount={amount}
                    onAmountChange={setAmount}
                    amountError={errors.amount}
                    contractField={
                      <div className='space-y-1'>
                        <FieldLabel>{t('form.contractReference')}</FieldLabel>
                        <EntityPicker<Contract>
                          key={`contract-${clientId}`}
                          queryKey={`contracts-${clientId}`}
                          preload
                          placeholder={t('form.select')}
                          fetcher={({ search, limit, page }) =>
                            contractsApi.list({
                              search,
                              limit,
                              page,
                              client_id: clientId,
                            })
                          }
                          getOptionLabel={contract =>
                            contract.number ??
                            contract.title ??
                            contract.id.slice(0, 8)
                          }
                          getOptionValue={contract => contract.id}
                          value={contractId || null}
                          initialLabel={
                            selectedContract?.data?.number ??
                            selectedContract?.data?.title ??
                            undefined
                          }
                          portalContainer={portalContainer}
                          onChange={value => setContractId(value)}
                          className={cn(
                            entityPickerClass,
                            'font-normal [&_svg]:opacity-100'
                          )}
                        />
                      </div>
                    }
                    warrantyFields={
                      <div className='space-y-1'>
                        <FieldLabel>{t('form.originalJob')}</FieldLabel>
                        <EntityPicker<Job>
                          key={`original-job-${clientId}`}
                          queryKey={`original-jobs-${clientId}-${id ?? 'new'}`}
                          disabled={!clientId}
                          preload={!!clientId}
                          placeholder={t('form.select')}
                          fetcher={({ search, limit, page }) =>
                            jobsApi.list({
                              search,
                              limit,
                              page,
                              client_id: clientId,
                              exclude_job_id: editing ? id : undefined,
                            })
                          }
                          getOptionLabel={job =>
                            `${job.reference ?? job.id.slice(0, 8)}${job.service_type ? ` - ${job.service_type}` : ''}`
                          }
                          getOptionValue={job => job.id}
                          value={originalJobId || null}
                          initialLabel={
                            jobData?.data?.original_job
                              ? `${jobData.data.original_job.reference ?? jobData.data.original_job.id.slice(0, 8)}${jobData.data.original_job.service_type ? ` - ${jobData.data.original_job.service_type}` : ''}`
                              : undefined
                          }
                          portalContainer={portalContainer}
                          onChange={value => void onOriginalJobSelect(value)}
                          className={cn(
                            entityPickerClass,
                            'font-normal [&_svg]:opacity-100'
                          )}
                        />
                        {errors.original_job_id && (
                          <p className='text-xs text-destructive'>
                            {errors.original_job_id}
                          </p>
                        )}
                      </div>
                    }
                  />
                  {errors.intervention_type && (
                    <p className='text-xs text-destructive'>
                      {errors.intervention_type}
                    </p>
                  )}
                </SectionCard>

                <SectionCard step={5} title={t('form.technicianNote')}>
                  <Textarea
                    id='note'
                    rows={3}
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder={t('form.technicianNotePlaceholder')}
                    className={cn('h-20 resize-none', FORM_TEXTAREA_CLASS)}
                  />
                </SectionCard>

                <SectionCard step={6} title={t('form.assignTechnician')}>
                  <AssignTechnicianSection
                    selected={technicians}
                    onChange={setTechnicians}
                    context={{
                      quote_id: quoteId || undefined,
                      contract_id: contractId || undefined,
                      service_type: serviceType || undefined,
                      intervention_type: interventionType || undefined,
                      scheduled_date: scheduledDate || undefined,
                      start_time: startTime || undefined,
                      end_time: endTime || undefined,
                      priority,
                      latitude,
                      longitude,
                      // In edit mode, exclude this job so a technician already assigned to
                      // it is not flagged busy by their own assignment here.
                      exclude_job_id: editing ? id : undefined,
                    }}
                  />
                </SectionCard>

                {/* Feedback #13 - Attach files (upload / client documents / library). */}
                <SectionCard
                  step={7}
                  title={t('form.attachFiles')}
                  complete={attachments.length > 0}
                  collapsible
                >
                  <AttachFilesSection
                    hideHeading
                    hideUploadLabel
                    brandTabs
                    value={attachments}
                    onChange={setAttachments}
                    readOnly={!canEditAgenda}
                    clientId={clientId || undefined}
                    uploadFn={uploadAttachment}
                    listClientDocsFn={clientId ? listClientDocs : undefined}
                    listLibraryFn={listLibraryDocs}
                    libraryTarget='job'
                    preloadLibrary
                    onRename={renameAttachment}
                    uploadFormatsHint={t('form.attachmentsFormatHint')}
                  />
                </SectionCard>

                {!editing && (
                  <SectionCard
                    step={8}
                    title={t('form.repeatSchedule')}
                    collapsible
                  >
                    <RepeatScheduleSection
                      value={repeat}
                      onChange={setRepeat}
                    />
                  </SectionCard>
                )}
              </>
            )}
          </div>

          <footer className='tf-right-drawer-footer justify-end'>
            <Button
              type='button'
              variant='outline'
              className={outlineBtnClass}
              onClick={closeRoute}
              disabled={pending}
            >
              {t('form.cancel')}
            </Button>
            <Button
              type='button'
              className={primaryBtnClass}
              onClick={submit}
              disabled={
                pending || ((editing || !!duplicateFromId) && isLoading)
              }
            >
              {!editing && (
                <Check
                  className='size-4 shrink-0 stroke-[2.2]'
                  aria-hidden='true'
                />
              )}
              {editing ? t('form.saveChanges') : t('form.saveIntervention')}
            </Button>
          </footer>
        </DialogContent>
      </Dialog>

      <ClientFormDialog
        target={clientFormOpen ? { mode: 'create' } : null}
        onClose={() => setClientFormOpen(false)}
        onSuccess={client => {
          onClientSelect(client.id, client)
          setClientFormOpen(false)
        }}
      />
    </>
  )
}
